import PropTypes from "prop-types"
import React from "react"
import { useSelector } from "react-redux"
import { matchPath, useLocation, useSearchParams } from "react-router-dom"

import { useTranslate } from "context/translateContext"
import { ELIGIBILITY_STATUS } from "data/constants"
import { useEplUserOnboardingQuery } from "data/queries/epl/onboarding"
import {
  getEplEligibilityStatus,
  isOnboardedToEPL,
  planSelectionStatus,
} from "shared/helpers/epl"

import MenuEntryFactory from "./factory/MenuEntry"
import useExpandableMenuTracker from "./useExpandableMenuTracker"

import styles from "./MenuRenderer.module.css"

export default function MenuRenderer(props) {
  const { t: translate } = useTranslate()
  const { accounts, activeAccount, activePlatform } = props
  const specialExcept =
    activePlatform === "amazon"
      ? ["preTrialESB", "isReturningAmazonUser"]
      : ["preTrialESB"]

  const [searchParams] = useSearchParams()
  const location = useLocation()

  const userId = useSelector(state => state.user.id)
  const eplOnboarding = useEplUserOnboardingQuery(userId)

  const getAdvertisingMenuEntry = () => {
    // mapping to userAccounts data format that is returned from newer react-query useUserAccountsQuery
    const userAccounts = {
      data: {
        accounts: accounts.ebay.map(id => ({ account_id: id })),
      },
    }

    const isSellerOnboarded =
      isOnboardedToEPL(eplOnboarding?.data) &&
      getEplEligibilityStatus(userAccounts, activeAccount) !==
        ELIGIBILITY_STATUS.NOT_PROCESSED
    const isPlanSelected = planSelectionStatus(eplOnboarding?.data)

    if (isSellerOnboarded && isPlanSelected) {
      const reportsPathPattern = "/:platform/:account/advertising/reports"
      const isOnReportsPage = matchPath(reportsPathPattern, location.pathname)
      const reportsPath = isOnReportsPage
        ? `${reportsPathPattern}?${searchParams.toString()}`
        : reportsPathPattern

      return {
        label: translate("Advertising"),
        permissions: {
          allowed: ["ebay"],
        },
        items: [
          {
            label: translate("Advertising report"),
            parent: [translate("Advertising")],
            path: reportsPath,
          },
          {
            label: translate("Your items"),
            parent: [translate("Advertising")],
            path: "/:platform/:account/advertising/items",
          },
          {
            label: translate("Settings"),
            parent: [translate("Advertising")],
            path: "/:platform/:account/advertising/settings",
          },
        ],
      }
    }

    return {
      label: translate("Advertising"),
      path: "/:platform/:account/advertising",
      permissions: {
        allowed: ["ebay"],
      },
    }
  }

  const menuEntries = [
    {
      label: translate("Dashboard"),
      path: "/:platform/:account/dashboard",
      permissions: {
        except: ["preTrialESB"],
      },
    },
    {
      label: translate("Internationalization"),
      items: [
        {
          label: translate("Items"),
          parent: [translate("Internationalization")],
          path: "/:platform/:account/products",
        },
        {
          label: translate("Orders"),
          parent: [translate("Internationalization")],
          path: "/:platform/:account/orders",
        },
        {
          label: translate("Settings"),
          items: [
            {
              label: translate("Listing rules"),
              parent: [
                translate("Internationalization"),
                translate("Settings"),
              ],
              path: "/:platform/:account/settings/listing-rules",
            },
            {
              label: translate("Translation settings"),
              parent: [
                translate("Internationalization"),
                translate("Settings"),
              ],
              path: "/:platform/:account/settings/translation-settings",
            },
            {
              label: translate("Price management"),
              parent: [
                translate("Internationalization"),
                translate("Settings"),
              ],
              path: "/:platform/:account/settings/price-management",
              permissions: {
                allowed: ["ebay", "amazon"],
                except: specialExcept,
              },
            },
            {
              label: translate("Shipping settings"),
              parent: [
                translate("Internationalization"),
                translate("Settings"),
              ],
              path: "/:platform/:account/settings/shipping-settings",
              permissions: {
                allowed: ["ebay"],
              },
            },
          ],
        },
      ],
    },
    ...(activePlatform === "ebay" ? [getAdvertisingMenuEntry()] : []),
    {
      label: translate("Shipping"),
      path: "/shipping",
    },
    {
      label: translate("Returns"),
      path: "/returns",
    },
    {
      label: translate("Account settings"),
      items: [
        {
          label: translate("Plans"),
          parent: [translate("Account settings")],
          path: "/plans",
        },
        {
          label: translate("Invoices"),
          parent: [translate("Account settings")],
          path: "/user/invoices",
        },
        {
          label: translate("Financial details"),
          parent: [translate("Account settings")],
          path: "/user/profile/financial-details",
          permissions: {
            allowed: ["paying", "subscribed"],
          },
        },
        {
          label: translate("Accounts"),
          parent: [translate("Account settings")],
          path: "/user/accounts",
          permissions: {
            allowed: ["ebay"],
          },
        },
        {
          label: translate("Contact details"),
          parent: [translate("Account settings")],
          path: "/user/profile/contact-details",
        },
        {
          label: translate("Login & Password"),
          parent: [translate("Account settings")],
          path: "/user/profile/login-details",
        },
        {
          label: translate("Third party tools"),
          parent: [translate("Account settings")],
          path: "/user/profile/third-party-tools",
        },
      ],
    },
  ]

  const account = getAccount(accounts, activeAccount, activePlatform)
  const { expandedTracker, setLastClicked, expandedMenuItems } =
    useExpandableMenuTracker()

  return (
    <div className={styles.root}>
      <ul className={["l-unstyled", styles.navigationDeep1].join(" ")}>
        {menuEntries?.map(entry => (
          <MenuEntryFactory
            setExpandedMenuItem={expandedTracker}
            expandedMenuItems={expandedMenuItems}
            key={entry.label}
            activePlatform={activePlatform}
            account={account}
            entry={entry}
            setLastClicked={setLastClicked}
            depth={1}
          />
        ))}
      </ul>
    </div>
  )
}

MenuRenderer.propTypes = {
  accounts: PropTypes.object.isRequired,
  activeAccount: PropTypes.number.isRequired,
  activePlatform: PropTypes.string.isRequired,
}

function getAccount(accounts, activeAccount, platform) {
  const properPlatform = ["ebay", "amazon"].includes(platform)
    ? platform
    : "ebay"

  return accounts[properPlatform].indexOf(activeAccount) > -1
    ? activeAccount
    : accounts[properPlatform][0]
}
